import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../contexts/EventContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useBackground } from '../contexts/BackgroundContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { ChatBubble } from './ChatBubble';
import { ScreenIdentifierBar } from './ScreenIdentifierBar';
import ErrorReportModal from './ErrorReportModal';
import { MobileHeader } from './MobileHeader';
import { MobileNavOverlay } from './MobileNavOverlay';
import { DesktopSidebar } from './DesktopSidebar';
import { EventContextBar } from './EventContextBar';
import { ChapterSelectionPopup } from './ChapterSelectionPopup';
import { LoginBroadcastModal } from './LoginBroadcastModal';
import { apiClient } from '../api/client';
import { getNavForRole, getNavForRoleAndModules, VIEW_CATEGORIES } from '../config/navConfig';
import { isCustomerRole } from '../config/navConfig';
import { CustomerCommandPalette } from './CustomerCommandPalette';
import { useWorldTheme } from '../hooks/useWorldTheme';
import { useMobileBottomNavConfig } from '../hooks/useMobileBottomNavConfig';
import { usePanelImages } from '../hooks/usePanelImages';
import { SECTION_KEYS, SECTION_LABELS, MOBILE_BREAKPOINT } from './attendee-mobile/constants';

/**
 * Inline swipe panel header for Attendee users on narrow viewports (≤768px).
 * Renders a 180px tall image banner with section name, navigation arrows, and dot indicators.
 * Images are fetched from /panel-images API with gradient fallbacks.
 */
function AttendeeSwipeHeader({ currentPath }: { currentPath: string }) {
  const { panelImages } = usePanelImages();

  // Map current route to section index
  const pathToIndex: Record<string, number> = {
    '/my-weekend': 0,
    '/guest-toolkit': 1,
    '/events': 2,
    '/marketplace': 3,
    '/announcements': 4,
    '/chapters': 5,
    '/krio-fun-facts': 6,
    '/knowledge': 7,
    '/story-builder/my-story': 8,
    '/settings': 9,
  };
  const currentIndex = pathToIndex[currentPath] ?? 0;
  const sectionKey = SECTION_KEYS[currentIndex];
  const sectionName = SECTION_LABELS[currentIndex] ?? 'My Weekend';
  const imageUrl = panelImages[sectionKey] ?? '';

  return (
    <div style={swipeStyles.wrapper}>
      <div style={{
        ...swipeStyles.panel,
        backgroundImage: imageUrl ? `url(${imageUrl})` : `linear-gradient(135deg, #1a6b3c, #2dd4bf)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}>
        <div style={swipeStyles.overlay}>
          <h2 style={swipeStyles.title}>{sectionName}</h2>
        </div>
      </div>
      {/* Dot indicators */}
      <div style={swipeStyles.dots}>
        {SECTION_KEYS.map((_, i) => (
          <span
            key={i}
            style={{
              ...swipeStyles.dot,
              ...(i === currentIndex ? swipeStyles.dotActive : {}),
            }}
          />
        ))}
      </div>
    </div>
  );
}

const swipeStyles: Record<string, React.CSSProperties> = {
  wrapper: { width: '100%' },
  panel: {
    position: 'relative',
    width: '100%',
    height: '180px',
    overflow: 'hidden',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: '#fff',
    fontSize: '1.4rem',
    fontWeight: 700,
    textAlign: 'center' as const,
    padding: '0 32px',
    margin: 0,
    textShadow: '0 1px 3px rgba(0,0,0,0.4)',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 0',
    backgroundColor: '#fff',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#d1d5db',
    transition: 'background-color 0.2s',
  },
  dotActive: {
    backgroundColor: '#1a6b3c',
    transform: 'scale(1.2)',
  },
};

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  // The selected event scopes all data pages. Keying the routed subtree off it
  // forces a remount (and thus a refetch of every useEffect([]) page) when the
  // operator switches events, so guests/seating/orders re-scope immediately.
  const { selectedEventId, enabledModules } = useEvents();
  const { isDark, collapsedViews, toggleViewCollapsed } = usePreferences();
  const { backgroundUrl } = useBackground();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBugModal, setShowBugModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const unreadRef = useRef(0);

  // World theming: customer workspace is the green "organization" world; the
  // KDU green palette makes it a safe default for legacy roles too.
  useWorldTheme(user?.role === 'Attendee' ? 'attendee' : 'organization');

  // ⌘K / Ctrl-K command palette — customer-tier workspace only (keeps the
  // legacy KDU attendee/organizer flows untouched).
  const paletteEnabled = isCustomerRole(user?.role || '');
  useEffect(() => {
    if (!paletteEnabled) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paletteEnabled]);

  // Live Help Desk: is this user staff, and how many open requests?
  // Drives showing the "Guest Help Desk" nav item (with an open-count badge) to
  // team members who are below Organizer, on BOTH mobile and desktop menus, so
  // they can monitor new requests from their phones.
  const [isHelpDeskStaff, setIsHelpDeskStaff] = useState(false);
  const [helpDeskOpenCount, setHelpDeskOpenCount] = useState(0);

  // Compute role-filtered navigation views. For customer-tier roles the nav is
  // ALSO gated by the selected event's enabled modules: when an event is in
  // scope we pass its module list; when none is selected we pass null so only
  // core (non-module) items show. Non-customer roles ignore modules entirely.
  // Tenant-level pillar modules (e.g. 'giving') — event-independent, from the
  // tenant's enabledModules via /auth/me. Fetched once per user.
  const [tenantModules, setTenantModules] = useState<string[]>([]);
  // Tenant display name — drives the persistent "which org am I in" banner for
  // customer-tier users.
  const [tenantName, setTenantName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!user) { setTenantModules([]); setTenantName(null); return; }
    apiClient<{ tenantModules?: string[]; tenantName?: string | null }>('/auth/me')
      .then((res) => {
        if (cancelled) return;
        setTenantModules(res?.tenantModules ?? []);
        setTenantName(res?.tenantName ?? null);
      })
      .catch(() => { if (!cancelled) { setTenantModules([]); setTenantName(null); } });
    return () => { cancelled = true; };
  }, [user]);

  const baseNavViews = useMemo(() => {
    const role = user?.role || '';
    if (!isCustomerRole(role)) return getNavForRole(role);
    const modules = selectedEventId ? enabledModules : null;
    return getNavForRoleAndModules(role, modules, tenantModules);
  }, [user?.role, selectedEventId, enabledModules, tenantModules]);

  // Check help-desk staff authorization once (per user)
  useEffect(() => {
    let cancelled = false;
    if (!user) { setIsHelpDeskStaff(false); return; }
    apiClient<{ isStaff: boolean }>('/help-desk/admin/authorization')
      .then((res) => { if (!cancelled) setIsHelpDeskStaff(!!res.isStaff); })
      .catch(() => { if (!cancelled) setIsHelpDeskStaff(false); });
    return () => { cancelled = true; };
  }, [user]);

  // Poll open-request count while staff (so the badge stays fresh for monitoring)
  useEffect(() => {
    if (!isHelpDeskStaff) { setHelpDeskOpenCount(0); return; }
    let cancelled = false;
    async function fetchCount() {
      try {
        // Endpoint returns a raw number (count of Open + InProgress inquiries)
        const data = await apiClient<number>('/help-desk/admin/open-count');
        if (!cancelled) setHelpDeskOpenCount(typeof data === 'number' ? data : 0);
      } catch { /* non-critical */ }
    }
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isHelpDeskStaff]);

  // Augment nav: ensure the Guest Help Desk item is present for staff (incl.
  // below-Organizer) and carries the open-request count as a badge.
  const navViews = useMemo(() => {
    if (!isHelpDeskStaff) return baseNavViews;
    const badge = helpDeskOpenCount > 0 ? helpDeskOpenCount : undefined;
    const helpDeskItem = {
      path: '/help-desk',
      label: 'Guest Help Desk',
      icon: '🪘',
      viewId: 'overview' as const,
      badge,
    };
    let injected = false;
    const next = baseNavViews.map((cat) => {
      const existingIdx = cat.items.findIndex((i) => i.path === '/help-desk');
      if (existingIdx >= 0) {
        injected = true;
        const items = [...cat.items];
        items[existingIdx] = { ...items[existingIdx], badge };
        return { ...cat, items };
      }
      return cat;
    });
    if (injected) return next;
    // Item not present (below-Organizer staff) — add it to the Overview category.
    const overviewIdx = next.findIndex((c) => c.category.id === 'overview');
    if (overviewIdx >= 0) {
      const cat = next[overviewIdx];
      next[overviewIdx] = { ...cat, items: [...cat.items, helpDeskItem] };
      return next;
    }
    // Overview category was dropped (empty for this role, e.g. a Delegate) —
    // recreate it so the Help Desk item still shows. Insert it at the top.
    const overviewCategory = VIEW_CATEGORIES.find((c) => c.id === 'overview');
    if (overviewCategory) {
      return [{ category: overviewCategory, items: [helpDeskItem] }, ...next];
    }
    return next;
  }, [baseNavViews, isHelpDeskStaff, helpDeskOpenCount]);

  // useMediaQuery returns true when viewport width ≤ 1023px (i.e., < 1024px)
  const isMobile = useMediaQuery(1023);

  // Show the persistent event context bar for customer-tier roles when an
  // event is in scope, so every page shows which event you're working on.
  const showEventBar = isCustomerRole(user?.role || '') && !!selectedEventId;

  // Persistent organization banner for customer-tier users — always visible so
  // an org user knows which organization's workspace they're in.
  const showOrgBanner = isCustomerRole(user?.role || '') && !!tenantName;
  const orgBanner = showOrgBanner ? (
    <div style={layoutStyles.orgBanner}>
      <span style={layoutStyles.orgBannerIcon} aria-hidden="true">🏢</span>
      <span style={layoutStyles.orgBannerName}>{tenantName}</span>
      <span style={layoutStyles.orgBannerTag}>workspace</span>
    </div>
  ) : null;

  const { enabled: bottomNavEnabled } = useMobileBottomNavConfig();

  // Close MobileNavOverlay automatically when viewport crosses 768px threshold
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen]);

  // Poll for unread notifications and show toast on new ones (single poller for the whole app)
  useEffect(() => {
    async function fetchUnread() {
      try {
        const data = await apiClient<{ unreadCount: number }>('/announcements/unread-count');
        const newCount = data.unreadCount;
        if (newCount > unreadRef.current && unreadRef.current > 0) {
          setShowToast(true);
          setTimeout(() => setShowToast(false), 6000);
        }
        unreadRef.current = newCount;
      } catch {
        // Non-critical
      }
    }
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // Reduced from 15s to 30s
    return () => clearInterval(interval);
  }, []);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  function handleMenuToggle() {
    setMobileMenuOpen((prev) => !prev);
  }

  function handleMenuClose() {
    setMobileMenuOpen(false);
  }

  // Mobile layout (< 768px): MobileHeader + MobileNavOverlay + full-width main
  if (isMobile) {
    return (
      <div style={layoutStyles.wrapper}>
        {paletteEnabled && <CustomerCommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />}
        <MobileHeader onMenuToggle={handleMenuToggle} isMenuOpen={mobileMenuOpen} />
        <MobileNavOverlay
          isOpen={mobileMenuOpen}
          onClose={handleMenuClose}
          navViews={navViews}
          collapsedViews={collapsedViews}
          onToggleView={toggleViewCollapsed}
          user={user}
          onLogout={handleLogout}
        />
        <main style={{
          ...layoutStyles.mainMobile,
          ...(bottomNavEnabled ? { paddingBottom: '60px' } : {}),
          ...(isDark ? { backgroundColor: '#1a1a2e', color: '#e0e0e0' } : {}),
          ...(backgroundUrl ? {
            backgroundImage: `url(${backgroundUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          } : {}),
        }}>
          {showToast && (
            <div style={layoutStyles.toast} onClick={() => { setShowToast(false); navigate('/announcements'); }}>
              🔔 You have new notifications! Tap to view.
            </div>
          )}
          {orgBanner}
          {showEventBar && <EventContextBar />}
          <div key={selectedEventId || 'no-event'}>{children}</div>
          <ChatBubble />
          <ErrorReportModal isOpen={showBugModal} onClose={() => setShowBugModal(false)} />
        </main>
        <ChapterSelectionPopup />
        <LoginBroadcastModal />
      </div>
    );
  }

  // Desktop layout (≥ 768px): DesktopSidebar + main with 260px margin
  return (
    <div style={layoutStyles.wrapper}>
      {paletteEnabled && <CustomerCommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />}
      <DesktopSidebar
        user={user}
        navViews={navViews}
        collapsedViews={collapsedViews}
        onToggleView={toggleViewCollapsed}
        onLogout={handleLogout}
      />
      <main style={{
        ...layoutStyles.main,
        ...(isDark ? { backgroundColor: '#1a1a2e', color: '#e0e0e0' } : {}),
      }}>
        {showToast && (
          <div style={layoutStyles.toast} onClick={() => { setShowToast(false); navigate('/announcements'); }}>
            🔔 You have new notifications! Click to view.
          </div>
        )}
        {paletteEnabled && (
          <button style={layoutStyles.cmdkFab} onClick={() => setPaletteOpen(true)} aria-label="Open command palette (⌘K)" title="Search or jump to… (⌘K)">
            🔍 <span style={{ opacity: 0.7 }}>Search</span> <kbd style={layoutStyles.cmdkFabKbd}>⌘K</kbd>
          </button>
        )}
        {orgBanner}
        {showEventBar && <EventContextBar />}
        <div key={selectedEventId || 'no-event'}>{children}</div>
        <ChatBubble />
        <ScreenIdentifierBar onReportBug={() => setShowBugModal(true)} />
        <ErrorReportModal isOpen={showBugModal} onClose={() => setShowBugModal(false)} />
      </main>
      <ChapterSelectionPopup />
      <LoginBroadcastModal />
    </div>
  );
}

const layoutStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    minHeight: '100vh',
  },
  orgBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    backgroundColor: '#0f2d1f',
    color: '#ffffff',
    fontSize: '0.85rem',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  orgBannerIcon: { fontSize: '0.95rem', lineHeight: 1 },
  orgBannerName: { fontWeight: 800, letterSpacing: '0.01em' },
  orgBannerTag: {
    fontSize: '0.62rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#9fc7af',
    marginLeft: 2,
  },
  main: {
    flex: 1,
    marginLeft: '260px',
    backgroundColor: '#f5f7f5',
    minHeight: '100vh',
  },
  mainMobile: {
    flex: 1,
    marginLeft: 0,
    paddingTop: '56px',
    backgroundColor: '#f5f7f5',
    minHeight: '100vh',
  },
  cmdkFab: {
    position: 'fixed' as const,
    top: 16,
    right: 20,
    zIndex: 900,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: '1px solid var(--ep-accent-soft, #d7e3dc)',
    borderRadius: 999,
    padding: '7px 12px',
    fontSize: '0.8rem',
    color: 'var(--ep-accent, #1a6b3c)',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 10px -2px rgba(26,107,60,0.2)',
  },
  cmdkFabKbd: { fontSize: '0.68rem', background: 'var(--ep-accent-soft, #f1f5f4)', border: '1px solid var(--ep-accent-soft, #d7e3dc)', borderRadius: 5, padding: '1px 6px', color: 'var(--ep-accent, #1a6b3c)', fontWeight: 700 },
  toast: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 999,
    backgroundColor: '#e53935',
    color: '#fff',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    textAlign: 'center' as const,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(229,57,53,0.3)',
    animation: 'pulse-badge 2s infinite',
  },
};
